import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ScanLine, Trash2, RotateCcw, RotateCw, ArrowUp, ArrowDown, Plus, Loader2, Download, UploadCloud, Image as ImageIcon, CheckCircle2, Printer } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/pages/Residents";
import SmartScanner from "@/components/scanner/SmartScanner";
import { buildPdf, rotateDataUrl, paperDims, PAPER } from "@/lib/scanner/paper";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

function pad(n) { return String(n).padStart(2, "0"); }
function defaultName() {
  const d = new Date();
  return `Scan ${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}.${pad(d.getMinutes())}`;
}

export default function SmartScannerPage() {
  const navigate = useNavigate();
  const [size, setSize] = useState("A4");
  const [orientation, setOrientation] = useState("portrait");
  const [scanning, setScanning] = useState(false);
  const [pages, setPages] = useState([]);
  const [docType, setDocType] = useState("umum");
  const [name, setName] = useState(defaultName());
  const [uploading, setUploading] = useState(false);
  const pagesRef = useRef(null);

  const onComplete = (newPages) => {
    setPages((prev) => [...prev, ...newPages]);
    setScanning(false);
    // Arahkan pengguna langsung ke daftar halaman + aksi (Simpan/Unduh/Cetak)
    setTimeout(() => pagesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
  };

  const move = (idx, dir) => {
    setPages((prev) => {
      const arr = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return prev;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return arr;
    });
  };
  const removePage = (idx) => setPages((prev) => prev.filter((_, i) => i !== idx));
  const rotatePage = async (idx, deg) => {
    const pg = pages[idx];
    const r = await rotateDataUrl(pg.dataUrl, deg);
    setPages((prev) => prev.map((p, i) => (i === idx ? { ...p, ...r } : p)));
  };
  const downloadImage = (pg, i) => {
    const a = document.createElement("a");
    a.href = pg.dataUrl;
    a.download = `${name || "halaman"}-${i + 1}.jpg`;
    a.click();
  };

  const generateAndUpload = async () => {
    if (pages.length === 0) { toast.error("Belum ada halaman untuk disimpan"); return; }
    setUploading(true);
    try {
      const blob = buildPdf(pages, size, orientation);
      const filename = `${(name || defaultName()).replace(/[^\w\s.-]/g, "")}.pdf`;
      const fd = new FormData();
      fd.append("file", new File([blob], filename, { type: "application/pdf" }));
      fd.append("document_type", docType);
      fd.append("paper_size", size);
      fd.append("orientation", orientation);
      fd.append("pages", String(pages.length));
      fd.append("original_filename", filename);
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Dokumen tersimpan ke penyimpanan Telegram");
      setPages([]);
      setName(defaultName());
      navigate("/dokumen");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploading(false);
    }
  };

  const downloadPdfOnly = () => {
    if (pages.length === 0) return;
    const blob = buildPdf(pages, size, orientation);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(name || "document").replace(/[^\w\s.-]/g, "")}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const printPages = () => {
    if (pages.length === 0) { toast.error("Belum ada halaman untuk dicetak"); return; }
    const w = window.open("", "_blank");
    if (!w) { toast.error("Popup diblokir. Izinkan popup untuk mencetak, atau gunakan Unduh PDF."); return; }
    // Ukuran kertas fisik memakai DIMENSI mm EKSPLISIT (F4 bukan keyword CSS valid → dulu jatuh ke A4).
    const { w: pw, h: ph } = paperDims(size, orientation);
    const imgs = pages.map((pg) => `<div class="pg"><img src="${pg.dataUrl}" /></div>`).join("");
    w.document.write(`<!doctype html><html><head><title>${(name || "Cetak").replace(/</g, "")}</title>
      <style>
        @page { size: ${pw}mm ${ph}mm; margin: 8mm; }
        html, body { margin: 0; padding: 0; }
        .pg { width: 100%; height: 100vh; display: flex; align-items: center; justify-content: center; page-break-after: always; }
        .pg:last-child { page-break-after: auto; }
        img { max-width: 100%; max-height: 100%; object-fit: contain; display: block; }
      </style></head>
      <body onload="window.focus();window.print();">${imgs}</body></html>`);
    w.document.close();
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {scanning && (
        <SmartScanner onComplete={onComplete} onClose={() => setScanning(false)} orientation={orientation} paperSize={size} />
      )}

      <PageHeader icon={ScanLine} title="Smart Scanner" subtitle="Pindai dokumen jadi PDF rapi — diproses lokal di perangkat, tanpa AI berbayar" />

      {/* Config */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
        <div>
          <div className="text-sm font-medium text-slate-700 mb-2">Ukuran Kertas</div>
          <div className="flex gap-2">
            {Object.keys(PAPER).map((k) => (
              <button key={k} onClick={() => setSize(k)} data-testid={`paper-${k}`}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium border ${size === k ? "bg-[#064E3B] text-white border-[#064E3B]" : "border-slate-200 text-slate-600"}`}>
                {PAPER[k].label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="text-sm font-medium text-slate-700 mb-2">Orientasi</div>
          <div className="flex gap-2">
            {[["portrait", "Portrait"], ["landscape", "Landscape"]].map(([k, l]) => (
              <button key={k} onClick={() => setOrientation(k)} data-testid={`orient-${k}`}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium border ${orientation === k ? "bg-[#064E3B] text-white border-[#064E3B]" : "border-slate-200 text-slate-600"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <div className="text-sm font-medium text-slate-700 mb-2">Nama Dokumen</div>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="doc-name" className="rounded-xl" />
          </div>
          <div>
            <div className="text-sm font-medium text-slate-700 mb-2">Jenis Dokumen</div>
            <Input value={docType} onChange={(e) => setDocType(e.target.value)} placeholder="umum / KK / KTP / surat" data-testid="doc-type" className="rounded-xl" />
          </div>
        </div>
        <Button onClick={() => setScanning(true)} data-testid="open-scanner" className="w-full h-12 rounded-xl bg-[#064E3B] hover:bg-[#043E2F]">
          <ScanLine size={18} className="mr-2" /> Buka Scanner
        </Button>
        <p className="flex items-center justify-center gap-1.5 text-xs text-slate-400">
          <CheckCircle2 size={14} className="text-emerald-600" /> Diproses di server — tanpa unduhan mesin, langsung siap di perangkat apa pun
        </p>
      </div>

      {/* Pages manager */}
      {pages.length > 0 && (
        <div ref={pagesRef} className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading font-bold text-slate-800">{pages.length} Halaman siap</h3>
              <p className="text-xs text-slate-400 mt-0.5">Pilih aksi di bawah: Simpan ke arsip, Unduh PDF, atau Cetak</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => setScanning(true)} className="rounded-lg border-[#064E3B] text-[#064E3B]"><Plus size={15} className="mr-1" /> Tambah Halaman</Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-testid="pages-grid">
            {pages.map((pg, i) => (
              <div key={pg.id} className="border border-slate-200 rounded-xl overflow-hidden" data-testid={`page-item-${i}`}>
                <div className="relative bg-slate-100">
                  <img src={pg.dataUrl} alt={`hal ${i + 1}`} className="w-full h-40 object-contain" />
                  <span className="absolute top-1 left-1 bg-[#064E3B] text-white text-xs px-2 py-0.5 rounded-full">{i + 1}</span>
                </div>
                <div className="flex flex-wrap justify-center gap-1 p-2 bg-slate-50">
                  <IconMini onClick={() => move(i, -1)} title="Naik"><ArrowUp size={15} /></IconMini>
                  <IconMini onClick={() => move(i, 1)} title="Turun"><ArrowDown size={15} /></IconMini>
                  <IconMini onClick={() => rotatePage(i, -90)} title="Putar kiri"><RotateCcw size={15} /></IconMini>
                  <IconMini onClick={() => rotatePage(i, 90)} title="Putar kanan"><RotateCw size={15} /></IconMini>
                  <IconMini onClick={() => downloadImage(pg, i)} title="Simpan JPG"><ImageIcon size={15} /></IconMini>
                  <IconMini onClick={() => removePage(i)} title="Hapus" danger><Trash2 size={15} /></IconMini>
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-2 mt-5">
            <Button variant="outline" onClick={printPages} className="rounded-xl h-12 flex-1" data-testid="print-pages"><Printer size={17} className="mr-2" /> Cetak</Button>
            <Button variant="outline" onClick={downloadPdfOnly} className="rounded-xl h-12 flex-1" data-testid="download-pdf"><Download size={17} className="mr-2" /> Unduh PDF</Button>
            <Button onClick={generateAndUpload} disabled={uploading} className="rounded-xl h-12 flex-[2] bg-[#D97706] hover:bg-[#b96305]" data-testid="save-telegram">
              {uploading ? <Loader2 className="animate-spin mr-2" size={18} /> : <UploadCloud size={18} className="mr-2" />}
              Simpan ke Arsip
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function IconMini({ children, onClick, title, danger }) {
  return (
    <button onClick={onClick} title={title}
      className={`h-8 w-8 grid place-items-center rounded-lg border ${danger ? "border-red-200 text-red-600 hover:bg-red-50" : "border-slate-200 text-slate-600 hover:bg-slate-100"}`}>
      {children}
    </button>
  );
}

